import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery
} from "@tanstack/react-query";

import { getErrorMessage } from "utils/error";
import addressApi, { QUERY_SYNC_UNITS_STATUS } from "api/landlord/address";
import { instance } from "api/apiBase";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  Address,
  PaginatedResponse,
  PropertyKind,
  PropertyManagementSystemSyncStatus
} from "shared/types";
import { Unit } from "types/landlord/address";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { toast } from "react-toastify";
import { useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { DEFAULT_UNITS_QUERY } from "./constants";
import {
  EMPTY_UNITS_FILTERS,
  UnitsFilters
} from "./components/UnitsFiltersDrawer/types";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || 10;

export type FraudAlertsFilter = "with" | "without";

const buildFilterParams = (
  filters: UnitsFilters,
  listed: boolean | null,
  fraudAlerts: FraudAlertsFilter | null
) => ({
  listed: listed ?? undefined,
  fraud_alerts: fraudAlerts ?? undefined,
  states: filters.states.length ? filters.states : undefined,
  cities: filters.cities.length ? filters.cities : undefined,
  property_group_ids: filters.property_group_ids.length
    ? filters.property_group_ids
    : undefined,
  property_ids: filters.property_ids.length ? filters.property_ids : undefined,
  unit_ids: filters.unit_ids.length ? filters.unit_ids : undefined,
  owner_ids: filters.owner_ids.length ? filters.owner_ids : undefined,
  sources: filters.sources.length ? filters.sources : undefined,
  current_statuses: filters.current_statuses.length
    ? filters.current_statuses
    : undefined,
  needs_lockbox: filters.needs_lockbox || undefined
});

export const useUnitsTableController = ({
  query,
  isSyncing = false,
  available_for_application = null,
  sorting = [],
  listed = null,
  fraudAlerts = null,
  filters = EMPTY_UNITS_FILTERS
}: {
  query: string;
  isSyncing?: boolean;
  available_for_application?: boolean | null;
  sorting?: SortingState;
  listed?: boolean | null;
  fraudAlerts?: FraudAlertsFilter | null;
  filters?: UnitsFilters;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      unit: "",
      page: "1",
      per_page: String(DEFAULT_PER_PAGE),
      view: "table"
    },
    ["unit", "page", "per_page", "view"]
  );

  const [debouncedParams] = useDebouncedValue(searchParams.unit, 500);
  const isUnitsDashboard = query === DEFAULT_UNITS_QUERY;
  const isCardView = isUnitsDashboard && searchParams.view === "card";

  const effectiveListed = isCardView && listed === false ? null : listed;

  const filterParams = isUnitsDashboard
    ? buildFilterParams(filters, effectiveListed, fraudAlerts)
    : {};

  const { data, refetch, isFetching, isRefetching } = useQuery({
    queryKey: [
      query,
      {
        ...searchParams,
        unit: debouncedParams,
        sorting,
        isCardView,
        filterParams
      }
    ],
    queryFn: () =>
      instance
        .get(query, {
          params: {
            page: searchParams.page,
            search: debouncedParams,
            available_for_application,
            per_page: searchParams.per_page,
            order_direction: sorting[0]?.desc ? "desc" : "asc",
            live_on_listings: isCardView || undefined,
            ...filterParams
          }
        })
        .then(({ data }: { data: PaginatedResponse<Unit> }) => data),
    placeholderData: keepPreviousData,
    enabled: !isCardView
  });

  const cardParams = {
    search: debouncedParams,
    available_for_application,
    per_page: searchParams.per_page,
    live_on_listings: true,
    ...filterParams
  };

  const cardsQuery = useInfiniteQuery({
    queryKey: [query, "INFINITE", cardParams],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      instance
        .get(query, { params: { ...cardParams, page: pageParam } })
        .then(({ data }: { data: PaginatedResponse<Unit> }) => data),
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.page_count
        ? lastPage.current_page + 1
        : undefined,
    enabled: isCardView
  });

  const cardUnits = useMemo(
    () => (cardsQuery.data?.pages ?? []).flatMap((page) => page.items),
    [cardsQuery.data]
  );

  const queryProperties = useInfiniteQuery<
    PaginatedResponse<{
      kind: PropertyKind | null;
      address: Address;
      id: number;
      magic_link: string | null;
      name: string;
      total_applications: number;
      total_units: number;
    }>
  >({
    queryKey: ["infinite_properties", { searchTerm: debouncedSearchTerm }],
    queryFn: ({ pageParam }) =>
      addressApi
        .getProperties({
          page: Number(pageParam),
          searchTerm: debouncedSearchTerm
        })
        .catch((err) => {
          toast.error(getErrorMessage(err));
        }),
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: PaginatedResponse<{
        address: Address;
        id: number;
        magic_link: string | null;
        name: string;
        total_applications: number;
        total_units: number;
      }> | null
    ) =>
      lastPage && lastPage.has_more ? (lastPage.current_page || 0) + 1 : null
  });

  const { data: syncData, isError: syncError } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_UNITS_STATUS],
      queryFn: () => addressApi.getPropertyManagementSystemSyncStatus("units"),
      refetchInterval: 4000,
      enabled: isSyncing
    });

  return {
    isUnitsDashboard,
    isCardView,
    cardUnits,
    cardsQuery,
    data,
    refetch,
    debouncedParams,
    searchParams,
    setSearchParams,
    queryProperties,
    isFetching,
    isRefetching,
    syncData,
    syncError,
    setSearchTerm,
    searchTerm
  };
};
